import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DatePickerCustom from '@/components/custom-date-picker';
import { formatDayLong, formatPrice } from '@/lib/format-utils';
import SendBalanceInput from '../send-balance-input';
import { Business, Payment, Reservation } from '@turizi/shared';
import { usePayments } from '@/hooks/api-hooks';
import { localTenant } from '@/services/session-service';
import { Loader } from '@mantine/core';
import { DEFAULT_CURRENCY } from '@turizi/shared';
import { Link } from 'react-router-dom';
import {
    IconShoppingCart,
    IconArrowDownLeft,
    IconScale,
    IconArrowUpRight,
    IconInfoCircle,
    IconLink,
    IconLinkOff
} from '@tabler/icons-react';

export interface ReservationsMoneySummaryProps {
    business?: Business;
    reservations?: Reservation[];
    dateRange?: [string, string];
    onDateRangeChange?: (value: [string, string]) => void;
}

export default function ReservationsMoneySummary({
    business,
    reservations = [],
    dateRange,
    onDateRangeChange,
}: ReservationsMoneySummaryProps): JSX.Element {
    const { t } = useTranslation();
    const businessId = business?.id;

    const [internalDateRange, setInternalDateRange] = useState<[string, string]>(['', '']);
    const activeDateRange = dateRange || internalDateRange;
    const handleDateRangeChange = onDateRangeChange || setInternalDateRange;

    // Fetch payments for the business
    const {
        data: paymentsRes,
        isLoading: paymentsLoading
    } = usePayments(
        businessId ? { business_id: businessId } : {},
        { enabled: !!businessId }
    );
    const fetchedPayments: Payment[] = paymentsRes?.data || [];

    const ownedBusinessIds = useMemo(() => {
        return (localTenant()?.businesses || []).map((b: any) => b.id).filter(Boolean);
    }, []);

    const filteredReservationsByDate = useMemo(() => {
        const start = activeDateRange[0];
        const end = activeDateRange[1];
        if (!start && !end) return reservations;
        return reservations.filter(r => {
            if (start && (!r.start_day || r.start_day < start)) return false;
            if (end && (!r.start_day || r.start_day > end)) return false;
            return true;
        });
    }, [reservations, activeDateRange]);

    const { totalsByCurrency, lastPaymentDates, paymentsBreakdown } = useMemo(() => {
        const totals: Record<string, {
            total: number;
            paid: number;
            remaining: number;
            surplus: number;
        }> = {};
        const breakdown: Record<string, {
            assignedPaid: number;
            linkedCount: number;
            unassignedPaid: number;
            unlinkedCount: number;
        }> = {};
        const lastDates: Record<string, string> = {};

        // 1. Sum total sales from service bookings in the SELECTED PERIOD
        filteredReservationsByDate.forEach(r => {
            const resvTotals = r.price_per_currency || {};
            Object.entries(resvTotals).forEach(([curr, amt]) => {
                if (!totals[curr]) {
                    totals[curr] = {
                        total: 0,
                        paid: 0,
                        remaining: 0,
                        surplus: 0
                    };
                }
                if (!breakdown[curr]) {
                    breakdown[curr] = { assignedPaid: 0, linkedCount: 0, unassignedPaid: 0, unlinkedCount: 0 };
                }
                totals[curr].total += Number(amt || 0);
            });
        });

        // 2. Sum ALL actual client payments for this business (regardless of date range)
        const validPayments = fetchedPayments.filter((p: any) => {
            if (ownedBusinessIds.length > 0) {
                const isProviderPayout = ownedBusinessIds.includes(p.from_business_id || -1) &&
                    p.to_business_id && !ownedBusinessIds.includes(p.to_business_id);
                if (isProviderPayout) return false;
            }
            return true;
        });

        validPayments.forEach((p: any) => {
            const curr = p.currency || DEFAULT_CURRENCY;
            if (!totals[curr]) {
                totals[curr] = { total: 0, paid: 0, remaining: 0, surplus: 0 };
            }
            if (!breakdown[curr]) {
                breakdown[curr] = { assignedPaid: 0, linkedCount: 0, unassignedPaid: 0, unlinkedCount: 0 };
            }
            const amt = Number(p.amount || 0);
            const refund = Number(p.refunded_amount || 0);
            const netAmt = Math.max(0, amt - refund);
            totals[curr].paid += netAmt;

            const allocatedRes = (p.reservations || []).reduce((acc: number, r: any) => {
                return acc + Number(r.payment_reservation?.amount ?? r.PaymentReservation?.amount ?? 0);
            }, 0);
            const allocatedBk = (p.service_bookings || []).reduce((acc: number, b: any) => {
                return acc + Number(b.payment_service_booking?.amount ?? b.PaymentServiceBooking?.amount ?? 0);
            }, 0);
            const totalAllocated = allocatedRes + allocatedBk;

            const paymentAssigned = Math.min(netAmt, totalAllocated);
            const paymentUnassigned = Math.max(0, netAmt - totalAllocated);

            breakdown[curr].assignedPaid += paymentAssigned;
            breakdown[curr].unassignedPaid += paymentUnassigned;

            if (totalAllocated > 0) {
                breakdown[curr].linkedCount += 1;
            }
            if (paymentUnassigned > 0.01) {
                breakdown[curr].unlinkedCount += 1;
            }

            const pDate = p.payment_date || p.created_at;
            if (pDate) {
                const dateStr = typeof pDate === 'string' ? pDate.slice(0, 10) : new Date(pDate).toISOString().slice(0, 10);
                if (!lastDates[curr] || dateStr > lastDates[curr]) {
                    lastDates[curr] = dateStr;
                }
            }
        });

        // 3. Compute remaining balance and surplus
        Object.keys(totals).forEach(curr => {
            const diff = totals[curr].total - totals[curr].paid;
            totals[curr].remaining = Math.max(0, diff);
            totals[curr].surplus = Math.max(0, -diff);
        });

        return { totalsByCurrency: totals, lastPaymentDates: lastDates, paymentsBreakdown: breakdown };
    }, [filteredReservationsByDate, fetchedPayments, ownedBusinessIds]);

    const hasTotals = Object.keys(totalsByCurrency).length > 0;

    return (
        <div className="space-y-4 flex items-start gap-6">
            {/* Top Toolbar Row */}
            <div className="flex flex-wrap items-center gap-3">
                {/* Date Range Filter */}
                <div className="space-y-2">
                    <DatePickerCustom
                        value={activeDateRange}
                        onChange={(val) => {
                            const [start, end] = val;
                            if ((start && end) || (!start && !end)) {
                                handleDateRangeChange(val);
                            }
                        }}
                        hideLabel={true}
                        placeholder={t('balance_dates')}
                        size="sm"
                        className="font-bold"
                    />
                    {/* Send Balance Action Button */}
                    {business && activeDateRange[0] && (
                        <SendBalanceInput
                            business={business}
                            startDay={activeDateRange[0]}
                            endDay={activeDateRange[1]}
                        />
                    )}
                </div>

                {paymentsLoading && <Loader size="xs" color="black" />}
            </div>

            {/* Financial Summary List Cards (matching balance-tab style) */}
            {!paymentsLoading && hasTotals && (
                <div className="space-y-3 w-[500px]">
                    <div className="flex flex-wrap gap-3">
                        {Object.entries(totalsByCurrency).map(([curr, { total, paid, remaining, surplus }]) => (
                            <div
                                key={curr}
                                className="bg-white border border-slate-200/90 rounded-xl overflow-hidden shadow-2xs w-full"
                            >
                                {/* Vertical List Items */}
                                <div className="divide-y divide-slate-100 text-xs">
                                    {/* 1. Total Money In (Total Venta del período) */}
                                    <div className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50/40 transition-colors">
                                        <div className="flex items-center gap-2.5">
                                            <div className="p-1.5 bg-blue-50 text-blue-600 rounded-md shrink-0">
                                                <IconShoppingCart size={15} stroke={2} />
                                            </div>
                                            <div>
                                                <span className="font-semibold text-slate-800">{t('reservations')}</span>
                                                {dateRange[0] && (
                                                    <div className="text-[10px] text-blue-600 font-medium bg-blue-50 border border-blue-200/60 px-1.5 py-0.5 rounded w-fit mt-0.5">
                                                        {t('selected_period')}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="font-semibold text-slate-800 text-sm">
                                            ${formatPrice(total)}{' '}
                                            <span className="text-[10px]  font-bold text-slate-500 bg-slate-100 border border-slate-200 px-1 py-0.5 rounded ml-0.5">
                                                {curr}
                                            </span>
                                        </div>
                                    </div>

                                    {/* 2. Money Received (Todos los pagos registrados) */}
                                    <div className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50/40 transition-colors">
                                        <div className="flex items-center gap-2.5">
                                            <div className="p-1.5 bg-emerald-50 text-emerald-600 rounded-md shrink-0">
                                                <IconArrowDownLeft size={15} stroke={2} />
                                            </div>
                                            <div>
                                                <span className="font-semibold text-slate-800">{t('total_money_received')}</span>
                                                <div className="text-[10px] text-slate-500 font-normal mt-0.5">
                                                    {lastPaymentDates[curr] && (
                                                        <span className="text-slate-800 font-light">
                                                            {t('last_payment')}: {formatDayLong(lastPaymentDates[curr])}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="font-semibold text-emerald-700 text-sm">
                                            ${formatPrice(paid)}{' '}
                                            <span className="text-[10px]  font-bold text-emerald-700 bg-emerald-50 border border-emerald-200/60 px-1 py-0.5 rounded ml-0.5">
                                                {curr}
                                            </span>
                                        </div>
                                    </div>

                                    {/* 2a. Assigned Payments Sub-row */}
                                    {paymentsBreakdown[curr] && (
                                        <>
                                            <div className="flex items-center justify-between pl-10 pr-4 py-2 bg-emerald-50/20 text-xs">
                                                <div className="flex items-center gap-2">
                                                    <div className="p-1 rounded bg-emerald-100/80 text-emerald-700 shrink-0">
                                                        <IconLink size={13} stroke={2} />
                                                    </div>
                                                    <span className="font-medium text-slate-700">
                                                        {t('assigned_to_reservations')}
                                                    </span>
                                                </div>
                                                <div className="font-semibold text-emerald-700 text-xs">
                                                    ${formatPrice(paymentsBreakdown[curr].assignedPaid)}{' '}
                                                    <span className="text-[9px]  font-bold text-emerald-700 bg-emerald-50 border border-emerald-200/60 px-1 py-0.5 rounded ml-0.5">
                                                        {curr}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* 2b. Unassigned Money Sub-row */}
                                            <div className="flex items-center justify-between pl-10 pr-4 py-2 bg-amber-50/20 text-xs">
                                                <div className="flex items-center gap-2">
                                                    <div className="p-1 rounded bg-amber-100/80 text-amber-700 shrink-0">
                                                        <IconLinkOff size={13} stroke={2} />
                                                    </div>
                                                    <span className="font-medium text-slate-700">
                                                        {t('unallocated_balance')}
                                                    </span>
                                                </div>
                                                <div className={`font-semibold text-xs ${paymentsBreakdown[curr].unassignedPaid > 0 ? 'text-amber-700' : 'text-slate-500'}`}>
                                                    ${formatPrice(paymentsBreakdown[curr].unassignedPaid)}{' '}
                                                    <span className={`text-[9px]  font-bold px-1 py-0.5 rounded ml-0.5 ${paymentsBreakdown[curr].unassignedPaid > 0 ? 'text-amber-700 bg-amber-50 border border-amber-200/60' : 'text-slate-500 bg-slate-100 border border-slate-200'}`}>
                                                        {curr}
                                                    </span>
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {/* 3. Remaining Amount (Resta) */}
                                    <div className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50/40 transition-colors">
                                        <div className="flex items-center gap-2.5">
                                            <div className={`p-1.5 rounded-md shrink-0 ${remaining > 0 ? 'bg-rose-50 text-rose-600' : 'bg-slate-100 text-slate-400'}`}>
                                                <IconScale size={15} stroke={2} />
                                            </div>
                                            <span className="font-semibold text-slate-800">{t('remaining_amount')}</span>
                                        </div>
                                        <div className={`font-semibold text-sm ${remaining > 0 ? 'text-rose-700' : 'text-slate-500'}`}>
                                            ${formatPrice(remaining)}{' '}
                                            <span className={`text-[10px]  font-bold px-1 py-0.5 rounded ml-0.5 ${remaining > 0 ? 'text-rose-700 bg-rose-50 border border-rose-200/60' : 'text-slate-500 bg-slate-100 border border-slate-200'}`}>
                                                {curr}
                                            </span>
                                        </div>
                                    </div>

                                    {/* 4. Surplus (A Favor) */}
                                    <div className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50/40 transition-colors">
                                        <div className="flex items-center gap-2.5">
                                            <div className={`p-1.5 rounded-md shrink-0 ${surplus > 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                                                <IconArrowUpRight size={15} stroke={2} />
                                            </div>
                                            <span className="font-semibold text-slate-800">{t('surplus')}</span>
                                        </div>
                                        <div className={`font-semibold text-sm ${surplus > 0 ? 'text-emerald-700' : 'text-slate-500'}`}>
                                            ${formatPrice(surplus)}{' '}
                                            <span className={`text-[10px]  font-bold px-1 py-0.5 rounded ml-0.5 ${surplus > 0 ? 'text-emerald-700 bg-emerald-50 border border-emerald-200/60' : 'text-slate-500 bg-slate-100 border border-slate-200'}`}>
                                                {curr}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Disclaimer Banner */}
                    <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200/80 rounded-lg text-xs text-slate-600 max-w-xl">
                        <IconInfoCircle size={16} className="text-slate-400 shrink-0" />
                        <span>
                            {t('balance_as_client_disclaimer')}{' '}
                            <Link
                                to={business?.id ? `/businesses/${business.id}/balance` : '/balance'}
                                className="text-blue-650 hover:underline font-semibold ml-1"
                            >
                                {t('check_full_balance')}
                            </Link>
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
}
